import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import { invalidatePLCategoryCache } from '../utils/plCategoryResolver';
import { normalizePosExtraction, buildPosApplyPlan, buildInitialPosMapping, posCategoryKey, cloverSalesToPosExtract, squareSalesToPosExtract } from '../utils/eopPosImport';
import { visuallyHiddenFileInputStyle } from '../utils/visuallyHiddenFileInputStyle';
import InventoryCategoryAssignmentTable from './InventoryCategoryAssignmentTable';

// Import POS Report — the EOP wizard step that reads an end-of-period report
// exported from the user's POS (Toast, Lightspeed, Aloha, Micros…) and fills
// the report's sales fields from it. No POS API connection is involved: the
// uploaded file is sent to the eop-pos-import edge function, which extracts
// totals + sales-by-category with OpenAI and proposes a P&L category match
// for each line. Matching is reviewed here — report categories that don't
// fit any existing P&L category automatically open the row-based assignment
// tool (the same InventoryCategoryAssignmentTable used in P&L setup), where
// each report category gets a searchable P&L selector and new P&L categories
// can be created on the spot. The wizard owns the extraction/mapping/applied
// state so it survives step navigation.
//
// Mapping edits are permanent: every change made in the mapping tool is
// upserted into pos_report_category_mappings keyed by the report category's
// name, and the saved choices override the AI match on every later upload.
// The AI only takes the first guess on report categories the store has never
// mapped (e.g. a category added to the POS this month) — and that guess can
// be corrected, which saves it, the next time around.

const BROWN = '#5A4A42';
const DARK_BROWN = '#3E2723';
const CREAM = '#FAF6F2';
const LIGHT_BORDER = '#E8DDD0';
const MUTED = '#8B7B73';
const FONT = "'Montserrat', sans-serif";

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatCurrency = (v) => currencyFormatter.format(Number.isFinite(v) ? v : 0);

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const IMAGE_MIMES = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
  heic: 'image/heic', gif: 'image/gif',
};
const isImageFile = (file) => {
  if ((file.type || '').startsWith('image/')) return true;
  const ext = (file.name || '').split('.').pop()?.toLowerCase();
  return !!IMAGE_MIMES[ext];
};

async function readImageBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result !== 'string' || !result.includes(',')) {
        reject(new Error('Cannot read image'));
        return;
      }
      const ext = (file.name || '').split('.').pop()?.toLowerCase();
      resolve({ mediaType: (file.type || '').trim() || IMAGE_MIMES[ext] || 'image/jpeg', data: result.split(',')[1] });
    };
    reader.onerror = () => reject(new Error('Error reading image'));
    reader.readAsDataURL(file);
  });
}

// Raw base64 of any file — PDFs go to the edge function whole (OpenAI reads
// them natively, scanned or not), so no pdf.js is needed on this page.
async function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result !== 'string' || !result.includes(',')) {
        reject(new Error('Cannot read file'));
        return;
      }
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsDataURL(file);
  });
}

// The reports page doesn't ship SheetJS; load it from the CDN on first use
// (same build the Add Items importer uses).
const scriptPromises = {};
function loadScriptOnce(src) {
  if (!scriptPromises[src]) {
    scriptPromises[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = () => { delete scriptPromises[src]; reject(new Error('Failed to load the spreadsheet reader — check your connection and try again.')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromises[src];
}

async function extractCsvText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result || '');
    reader.onerror = () => reject(new Error('Error reading CSV'));
    reader.readAsText(file);
  });
}

async function extractExcelText(file) {
  if (typeof XLSX === 'undefined') {
    await loadScriptOnce('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
  }
  return new Promise((resolve, reject) => {
    if (typeof XLSX === 'undefined') { reject(new Error('Spreadsheet reader is not loaded — export the report as PDF or CSV instead.')); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_csv(sheet));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Error reading Excel'));
    reader.readAsArrayBuffer(file);
  });
}

async function getEdgeHeaders() {
  const { data } = await supabase.auth.getSession();
  if (!data?.session?.access_token) throw new Error('Not authenticated. Please sign in and try again.');
  return {
    Authorization: `Bearer ${data.session.access_token}`,
    apikey: SUPABASE_KEY,
    'Content-Type': 'application/json',
  };
}

const CONFIDENCE_STYLES = {
  high: { background: '#E0F0E5', color: '#1a5c34' },
  medium: { background: '#FFF3CD', color: '#856404' },
  low: { background: '#FBEAEA', color: '#7A1F18' },
  none: { background: '#F0EAE4', color: '#8B7B73' },
};

const ConfidenceBadge = ({ level }) => (
  <span style={{
    fontSize: '0.66rem', padding: '1px 7px', borderRadius: 8, fontWeight: 700,
    whiteSpace: 'nowrap', ...(CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.none),
  }}>
    {level === 'none' ? 'no match' : `${level} match`}
  </span>
);

/**
 * Report-category → P&L-category matching tool for the POS import. This is the
 * SAME row-based assignment table used by the P&L setup step
 * (InventoryCategoryAssignmentTable), fed with the report's sales categories so
 * the import and setup flows share one interaction: a searchable P&L selector
 * per row, inline "add a new P&L category", and a "don't import" option — no
 * connector diagram.
 *
 * Link assignments flow back through onLink(idx, pnlCatId | '') exactly as
 * before, so the wizard mapping state and the permanent
 * pos_report_category_mappings persistence are unchanged. Creating a P&L
 * category is a real insert into pnl_categories.
 */
function PosReportCategoryMapper({
  storeId,
  pnlCategories,
  lines,            // [{ idx, name, grossSales, choice, explicitNone }]
  onLink,           // (idx, pnlCatId | '') — '' = don't import this line
  onCategoryAdded,  // (newCategoryRow)
  onClose,
}) {
  const unmatched = lines.filter((l) => l.grossSales != null && !l.choice && !l.explicitNone);

  // One assignment row per report category. The effective choice (the user's
  // selection, or the AI match when none) becomes the row's pnlId, so AI
  // matches show pre-selected; an explicit "don't import" is the excluded
  // state. Unmatched rows get a name-based suggestion from the shared table.
  const rows = useMemo(() => lines.map((line) => ({
    key: `pos:${line.idx}`,
    kind: 'pos',
    idx: line.idx,
    name: line.name,
    source: 'POS report',
    detail: line.grossSales != null ? `${formatCurrency(line.grossSales)} sales` : null,
    pnlId: line.choice ? Number(line.choice) : null,
    excluded: !!line.explicitNone,
    exclusionReason: line.explicitNone ? 'excluded' : null,
  })), [lines]);

  const handleAssignmentChange = async (row, change) => {
    // assign → import into that P&L category; exclude/unassign → leave the
    // line out of the import (both mean "not imported" for a POS report).
    onLink(row.idx, change.type === 'assign' ? String(change.pnlId) : '');
  };

  const handleAddPnlCategory = async (name) => {
    const t = name.trim();
    if (!t) throw new Error('Enter a category name.');
    const existing = pnlCategories.find((c) => c.name.toLowerCase() === t.toLowerCase());
    if (existing) return existing; // already there — the table will link to it
    const maxOrder = pnlCategories.reduce((m, c) => Math.max(m, c.sort_order || 0), -1);
    const { data, error: err } = await supabase
      .from('pnl_categories')
      .insert({ store_id: storeId, name: t, sort_order: maxOrder + 1 })
      .select().single();
    if (err) throw err;
    invalidatePLCategoryCache(storeId);
    onCategoryAdded(data);
    return data;
  };

  return (
    <div style={{ border: `1.5px solid ${BROWN}`, borderRadius: 12, padding: '0.85rem 1rem', marginBottom: '0.75rem', background: '#fff', fontFamily: FONT }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: '0.5rem' }}>
        <div style={{ fontWeight: 700, color: DARK_BROWN, fontSize: '0.88rem' }}>
          ⚙️ Match Report Categories to P&amp;L Categories
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: BROWN, color: CREAM, border: 'none', borderRadius: 8, padding: '4px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: FONT }}
        >
          ✓ Done
        </button>
      </div>
      <div style={{ fontSize: '0.76rem', color: MUTED, marginBottom: '0.75rem', lineHeight: 1.5 }}>
        We have found categories in your POS report that are not present in Countertop-APP.{' '}
        {unmatched.length > 0
          ? <>Your report has <strong>{unmatched.length}</strong> sales categor{unmatched.length === 1 ? 'y' : 'ies'} that {unmatched.length === 1 ? 'doesn’t' : 'don’t'} match any of your P&amp;L categories yet. Assign {unmatched.length === 1 ? 'it' : 'them'} to an existing P&amp;L category, or add a new one — you may not have created every category yet.</>
          : <>Every report category is matched to a P&amp;L category. Adjust any match below, or mark a category as not imported.</>}
        {' '}Changes here are <strong>saved to your mapping</strong> and applied automatically the next time you import a report with the same category names.
      </div>

      <InventoryCategoryAssignmentTable
        pnlCats={pnlCategories || []}
        rows={rows}
        showIntro={false}
        onAssignmentChange={handleAssignmentChange}
        onAddPnlCategory={handleAddPnlCategory}
        unitLabel={{ singular: 'report category', plural: 'report categories' }}
        unassignedConsequence="Unmatched sales categories are left out of the import."
        excludeOptionLabel="Don’t import this category"
        excludedStatusText="Not imported"
        showExclusionReasons={false}
      />
    </div>
  );
}

const EOPPosReportImport = ({
  storeId,
  pnlCategories,
  extraction,
  fileName,
  mapping,
  applied,
  onExtracted,
  onMappingChange,
  onCategoryAdded,
  onApply,
  onClear,
  // Clover stores can pull the period's sales straight from Clover instead of
  // (or in addition to) uploading a file — same review/apply pipeline, no AI.
  cloverConnected = false,
  cloverPeriodStart = '',
  cloverPeriodEnd = '',
  // Square stores get the identical pull option, against the same period window.
  squareConnected = false,
  squarePeriodStart = '',
  squarePeriodEnd = '',
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showMapper, setShowMapper] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cloverBusy, setCloverBusy] = useState(false);
  const [cloverError, setCloverError] = useState('');
  const [squareBusy, setSquareBusy] = useState(false);
  const [squareError, setSquareError] = useState('');
  const fileInputRef = useRef(null);
  const autoOpenedForRef = useRef(null);

  // The store's saved report-name → P&L category mappings
  // ({ normalizedName: pnlCatId (string) | null }, null = don't import).
  // The ref is the source of truth (so a freshly uploaded file can be seeded
  // with edits made moments ago); the state just triggers re-renders.
  const [savedMappings, setSavedMappings] = useState({});
  const savedMappingsRef = useRef({});
  const savedMappingsLoadRef = useRef(null);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    savedMappingsLoadRef.current = (async () => {
      const { data, error: err } = await supabase
        .from('pos_report_category_mappings')
        .select('normalized_name, pnl_category_id')
        .eq('store_id', storeId);
      if (err) {
        console.warn('Failed to load saved POS category mappings:', err.message);
        return;
      }
      const map = {};
      (data || []).forEach((r) => {
        map[r.normalized_name] = r.pnl_category_id != null ? String(r.pnl_category_id) : null;
      });
      if (!cancelled) {
        savedMappingsRef.current = map;
        setSavedMappings(map);
      }
    })();
    return () => { cancelled = true; };
  }, [storeId]);

  // Permanently remember a mapping choice for this report category name.
  // Called on every edit in the mapping tool, so next month's upload maps
  // the same report category the same way without asking again.
  const persistMappingChoice = useCallback(async (name, pnlCatId) => {
    const key = posCategoryKey(name);
    if (!storeId || !key) return;
    const previous = savedMappingsRef.current;
    savedMappingsRef.current = { ...previous, [key]: pnlCatId ? String(pnlCatId) : null };
    setSavedMappings(savedMappingsRef.current);
    const { error: err } = await supabase
      .from('pos_report_category_mappings')
      .upsert({
        store_id: storeId,
        report_category_name: name.trim(),
        normalized_name: key,
        pnl_category_id: pnlCatId ? Number(pnlCatId) : null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'store_id,normalized_name' });
    if (err) {
      console.warn('Failed to save POS category mapping:', err.message);
      // Keep the badge honest — the choice still applies to this import,
      // but it was not remembered.
      savedMappingsRef.current = previous;
      setSavedMappings(previous);
    }
  }, [storeId]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError('That file is larger than 10 MB. Export a summary report (1–3 pages) from your POS instead.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const n = (file.name || '').toLowerCase();
      const mime = (file.type || '').toLowerCase();
      let payload;
      if (isImageFile(file)) {
        const { mediaType, data } = await readImageBase64(file);
        payload = { fileType: 'image', mediaType, imageData: data };
      } else if (n.endsWith('.pdf') || mime === 'application/pdf') {
        // Send the PDF whole — the AI reads it directly, scanned pages included.
        payload = { fileType: 'pdf', pdfBase64: await readFileBase64(file) };
      } else if (n.endsWith('.xls') || n.endsWith('.xlsx') || mime.includes('spreadsheet') || mime.includes('excel')) {
        payload = { fileType: 'csv', text: await extractExcelText(file) };
      } else {
        // Anything else: best effort as text (covers .csv/.txt/.tsv and
        // export formats with unusual extensions). Reject only when the
        // content is clearly binary garbage.
        const text = await extractCsvText(file);
        const sample = String(text).slice(0, 4000);
        const binaryChars = (sample.match(/[\uFFFD\u0000-\u0008\u000E-\u001F]/g) || []).length;
        if (!sample.trim() || binaryChars / Math.max(sample.length, 1) > 0.05) {
          throw new Error('That file couldn\'t be read. Upload the report as a PDF, CSV, Excel file, or a photo/screenshot of it.');
        }
        payload = { fileType: 'csv', text };
      }

      const headers = await getEdgeHeaders();
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/eop-pos-import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'extract',
          ...payload,
          fileName: file.name,
          pnlCategories: (pnlCategories || []).map((c) => ({ id: String(c.id), name: c.name })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || `Report import failed (${resp.status}).`);

      const normalized = normalizePosExtraction(data, pnlCategories);
      if (normalized.categories.length === 0 && normalized.totals.grossSales == null && normalized.totals.netSales == null) {
        throw new Error('No sales figures were found in that file. Make sure it\'s a sales summary / end-of-period report from your POS.');
      }
      // Saved mappings beat the AI match for report categories the store has
      // mapped before; the AI only takes the first guess on new names.
      if (savedMappingsLoadRef.current) await savedMappingsLoadRef.current.catch(() => {});
      onExtracted({
        extraction: normalized,
        fileName: file.name,
        initialMapping: buildInitialPosMapping(normalized, savedMappingsRef.current, pnlCategories),
      });
    } catch (e) {
      setError(e.message || 'Failed to read the report.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Pull this period's sales straight from Clover and feed them through the
  // very same pipeline an uploaded report uses. clover-sales-pull returns
  // sales already resolved to P&L categories (no AI), which we reshape into
  // the edge function's extract format so normalize → map → apply is identical.
  const pullClover = async () => {
    if (cloverBusy || busy) return;
    const start = cloverPeriodStart;
    const end = cloverPeriodEnd;
    if (!start || !end) { setCloverError('Set the sales period start and end dates first.'); return; }
    if (start > end) { setCloverError('The period start must be on or before the end date.'); return; }
    setCloverBusy(true);
    setCloverError('');
    setError('');
    try {
      const headers = await getEdgeHeaders();
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/clover-sales-pull`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ store_id: storeId, period_start: start, period_end: end }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || `Clover pull failed (${resp.status}).`);

      const normalized = normalizePosExtraction(cloverSalesToPosExtract(data, pnlCategories), pnlCategories);
      if (normalized.categories.length === 0) {
        // No category lines at all means Clover returned no sales for this
        // period — not a mapping problem. Clover categories that don't yet line
        // up with a P&L category DO come back as lines and open the assignment
        // tool below, so they no longer dead-end here.
        throw new Error(Number(data?.net_sales_total) > 0
          ? 'Clover returned sales but no per-category breakdown for this period.'
          : 'Clover returned no sales for this period. Check the period dates and that this store is connected to the right Clover merchant.');
      }
      // Honor any saved report-name mappings the same way the file path does.
      if (savedMappingsLoadRef.current) await savedMappingsLoadRef.current.catch(() => {});
      onExtracted({
        extraction: normalized,
        fileName: `Clover sales · ${start} → ${end}`,
        initialMapping: buildInitialPosMapping(normalized, savedMappingsRef.current, pnlCategories),
      });
    } catch (e) {
      setCloverError(e.message || 'Failed to pull sales from Clover.');
    } finally {
      setCloverBusy(false);
    }
  };

  // Square pull — the Square analog of pullClover. square-sales-pull returns the
  // period's gross-by-category already resolved against the store's P&L
  // categories; we reshape it into the edge function's extract format so
  // normalize → map → apply is identical to a file upload or a Clover pull.
  const pullSquare = async () => {
    if (squareBusy || busy) return;
    const start = squarePeriodStart;
    const end = squarePeriodEnd;
    if (!start || !end) { setSquareError('Set the sales period start and end dates first.'); return; }
    if (start > end) { setSquareError('The period start must be on or before the end date.'); return; }
    setSquareBusy(true);
    setSquareError('');
    setError('');
    try {
      const headers = await getEdgeHeaders();
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/square-sales-pull`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ store_id: storeId, period_start: start, period_end: end }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || `Square pull failed (${resp.status}).`);

      const normalized = normalizePosExtraction(squareSalesToPosExtract(data, pnlCategories), pnlCategories);
      if (normalized.categories.length === 0) {
        throw new Error(Number(data?.net_sales_total) > 0
          ? 'Square returned sales but no per-category breakdown for this period.'
          : 'Square returned no sales for this period. Check the period dates and that this store is connected to the right Square merchant.');
      }
      if (savedMappingsLoadRef.current) await savedMappingsLoadRef.current.catch(() => {});
      onExtracted({
        extraction: normalized,
        fileName: `Square sales · ${start} → ${end}`,
        initialMapping: buildInitialPosMapping(normalized, savedMappingsRef.current, pnlCategories),
      });
    } catch (e) {
      setSquareError(e.message || 'Failed to pull sales from Square.');
    } finally {
      setSquareBusy(false);
    }
  };

  // The effective mapping choice for a report line: the user's selection
  // when one exists ('' = explicitly not imported), otherwise the AI match.
  const choiceFor = useCallback((idx, cat) => (
    Object.prototype.hasOwnProperty.call(mapping || {}, idx)
      ? (mapping[idx] || '')
      : (cat.matchedCatId || '')
  ), [mapping]);

  const mapperLines = useMemo(() => (
    (extraction?.categories || []).map((cat, idx) => ({
      idx,
      name: cat.name,
      grossSales: cat.grossSales,
      choice: choiceFor(idx, cat),
      explicitNone: Object.prototype.hasOwnProperty.call(mapping || {}, idx) && !mapping[idx],
    }))
  ), [extraction, mapping, choiceFor]);

  const needsMatch = useMemo(
    () => mapperLines.filter((l) => l.grossSales != null && !l.choice && !l.explicitNone),
    [mapperLines],
  );

  // Report categories that don't fit any existing P&L category automatically
  // bring up the matching tool (once per uploaded report).
  useEffect(() => {
    if (!extraction) {
      setShowMapper(false);
      autoOpenedForRef.current = null;
      return;
    }
    if (autoOpenedForRef.current === extraction) return;
    autoOpenedForRef.current = extraction;
    if (needsMatch.length > 0) setShowMapper(true);
  }, [extraction, needsMatch.length]);

  // Preview of what Apply will write, under the current mapping choices.
  const plan = useMemo(
    () => (extraction ? buildPosApplyPlan(extraction, mapping || {}) : null),
    [extraction, mapping],
  );

  const catNameById = useMemo(() => {
    const m = {};
    (pnlCategories || []).forEach((c) => { m[String(c.id)] = c.name; });
    return m;
  }, [pnlCategories]);

  const totalsChips = extraction ? [
    { label: 'Gross Sales', value: extraction.totals.grossSales },
    { label: 'Discounts', value: extraction.totals.discounts },
    { label: 'Comps', value: extraction.totals.comps },
    { label: 'Refunds', value: extraction.totals.refunds },
    { label: 'Net Sales', value: extraction.totals.netSales },
  ].filter((c) => c.value != null) : [];

  const ignoredChips = extraction ? [
    { label: 'Taxes', value: extraction.totals.taxes },
    { label: 'Service Charges', value: extraction.totals.serviceCharges },
  ].filter((c) => c.value != null) : [];

  const canApply = !!plan && (plan.appliedCategoryCount > 0
    || ['discounts', 'comps', 'refunds'].some((f) => (plan.unallocated[f] ?? 0) > 0));

  // Drag & drop anywhere on the import area — dropping while a report is
  // already loaded replaces it.
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {!extraction && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => { if (!busy) fileInputRef.current?.click(); }}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) fileInputRef.current?.click(); }}
          aria-label="Upload your POS end-of-period report"
          style={{
            border: `2px dashed ${dragOver ? '#5A4A42' : '#D4C4B0'}`,
            borderRadius: 12,
            background: dragOver ? '#F5EDE5' : '#FFFDF9',
            padding: '1.5rem 1rem', textAlign: 'center',
            cursor: busy ? 'default' : 'pointer',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <div style={{ fontSize: '1.6rem', marginBottom: '0.35rem' }}>📄</div>
          <div style={{ fontWeight: 700, color: '#3D2518', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
            {dragOver ? 'Drop your report to upload it' : 'Drag & drop your POS end-of-period report'}
          </div>
          <p style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '0.85rem', lineHeight: 1.5 }}>
            Export a sales summary for the period from your POS (Toast, Square, Lightspeed, Aloha,
            Micros…) and drop it here, or click to browse — PDF, CSV, Excel, a screenshot, or any
            file with the numbers in it. We'll read the sales, discounts, comps, and refunds and
            fill them into this report for you. No POS connection needed.
          </p>
          <button
            type="button"
            className="btn"
            style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.45rem 1.25rem', fontSize: '0.85rem', border: 'none' }}
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            disabled={busy}
          >
            {busy ? (<><span className="spinner-border spinner-border-sm me-2" />Reading report…</>) : 'Choose File'}
          </button>
        </div>
      )}

      {cloverConnected && !extraction && (
        <div style={{ marginTop: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.85rem' }}>
            <div style={{ flex: 1, height: 1, background: '#E8DDD0' }} />
            <span style={{ fontSize: '0.72rem', color: '#8B7B73', fontWeight: 600 }}>or pull straight from Clover</span>
            <div style={{ flex: 1, height: 1, background: '#E8DDD0' }} />
          </div>
          <div style={{ border: '1.5px solid #BCE6CD', background: '#F2FBF6', borderRadius: 12, padding: '1rem 1.1rem' }}>
            <div style={{ fontWeight: 700, color: '#0A6B3B', fontSize: '0.9rem', marginBottom: 4 }}>
              🍀 Connected to Clover
            </div>
            <p style={{ fontSize: '0.78rem', color: '#3E5A49', marginBottom: '0.85rem', lineHeight: 1.5 }}>
              Pull this period's sales by category straight from Clover — no file, no AI. We map your
              Clover categories to your P&amp;L categories automatically; review the matches and apply,
              just like an imported report. Discounts, comps, and refunds stay manual on the later steps.
            </p>
            <div style={{ fontSize: '0.78rem', color: '#3E5A49', marginBottom: '0.85rem', background: '#fff', border: '1px solid #BCE6CD', borderRadius: 9, padding: '0.55rem 0.75rem' }}>
              <strong>Clover sales period:</strong> {cloverPeriodStart || '…'} → {cloverPeriodEnd || '…'}
              <div style={{ fontSize: '0.72rem', color: '#4D6B5A', marginTop: 3 }}>
                Set automatically from the inventory counts selected for this report.
              </div>
            </div>

            {cloverError && (
              <div style={{ fontSize: '0.78rem', color: '#7A1F18', background: '#FBEAEA', borderRadius: 7, padding: '0.4rem 0.75rem', marginBottom: '0.7rem' }}>
                {cloverError}
              </div>
            )}
            <button
              type="button"
              onClick={pullClover}
              disabled={cloverBusy || !cloverPeriodStart || !cloverPeriodEnd}
              style={{
                background: '#00A551', color: '#fff', border: 'none', borderRadius: 10,
                fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.85rem',
                boxShadow: '0 1px 3px rgba(0,165,81,0.25)', fontFamily: FONT,
                cursor: cloverBusy || !cloverPeriodStart || !cloverPeriodEnd ? 'not-allowed' : 'pointer',
                opacity: cloverBusy || !cloverPeriodStart || !cloverPeriodEnd ? 0.6 : 1,
              }}
            >
              {cloverBusy
                ? (<><span className="spinner-border spinner-border-sm me-2" />Pulling Clover data…</>)
                : '🍀 Pull Clover data to end of period report'}
            </button>
          </div>
        </div>
      )}

      {squareConnected && !extraction && (
        <div style={{ marginTop: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.85rem' }}>
            <div style={{ flex: 1, height: 1, background: '#E8DDD0' }} />
            <span style={{ fontSize: '0.72rem', color: '#8B7B73', fontWeight: 600 }}>or pull straight from Square</span>
            <div style={{ flex: 1, height: 1, background: '#E8DDD0' }} />
          </div>
          <div style={{ border: '1.5px solid #CCE5FF', background: '#F5FAFF', borderRadius: 12, padding: '1rem 1.1rem' }}>
            <div style={{ fontWeight: 700, color: '#005FA3', fontSize: '0.9rem', marginBottom: 4 }}>
              🟦 Connected to Square
            </div>
            <p style={{ fontSize: '0.78rem', color: '#2D5A87', marginBottom: '0.85rem', lineHeight: 1.5 }}>
              Pull this period's sales by category straight from Square — no file, no AI. We map your
              Square categories to your P&amp;L categories automatically; review the matches and apply,
              just like an imported report. Discounts, comps, and refunds stay manual on the later steps.
            </p>
            <div style={{ fontSize: '0.78rem', color: '#2D5A87', marginBottom: '0.85rem', background: '#fff', border: '1px solid #CCE5FF', borderRadius: 9, padding: '0.55rem 0.75rem' }}>
              <strong>Square sales period:</strong> {squarePeriodStart || '…'} → {squarePeriodEnd || '…'}
              <div style={{ fontSize: '0.72rem', color: '#4D6B87', marginTop: 3 }}>
                Set automatically from the inventory counts selected for this report.
              </div>
            </div>

            {squareError && (
              <div style={{ fontSize: '0.78rem', color: '#7A1F18', background: '#FBEAEA', borderRadius: 7, padding: '0.4rem 0.75rem', marginBottom: '0.7rem' }}>
                {squareError}
              </div>
            )}
            <button
              type="button"
              onClick={pullSquare}
              disabled={squareBusy || !squarePeriodStart || !squarePeriodEnd}
              style={{
                background: '#005FA3', color: '#fff', border: 'none', borderRadius: 10,
                fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.85rem',
                boxShadow: '0 1px 3px rgba(0,95,163,0.25)', fontFamily: FONT,
                cursor: squareBusy || !squarePeriodStart || !squarePeriodEnd ? 'not-allowed' : 'pointer',
                opacity: squareBusy || !squarePeriodStart || !squarePeriodEnd ? 0.6 : 1,
              }}
            >
              {squareBusy
                ? (<><span className="spinner-border spinner-border-sm me-2" />Pulling Square data…</>)
                : '🟦 Pull Square data to end of period report'}
            </button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        style={visuallyHiddenFileInputStyle}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && (
        <div style={{ fontSize: '0.8rem', color: '#7A1F18', background: '#FBEAEA', borderRadius: 7, padding: '0.4rem 0.75rem', marginTop: '0.6rem' }}>
          {error}
        </div>
      )}

      {extraction && (
        <div style={{
          background: dragOver ? '#F5EDE5' : '#FFFDF9',
          border: `1.5px ${dragOver ? 'dashed #5A4A42' : 'solid #E8DDD0'}`,
          borderRadius: 12, padding: '1rem 1.25rem',
          transition: 'border-color 0.15s, background 0.15s',
        }}>
          {dragOver && (
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#5A4A42', marginBottom: '0.5rem' }}>
              Drop to replace the current report
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: '0.5rem' }}>
            <div style={{ fontWeight: 700, color: '#3D2518', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>📄</span>
              <span>{fileName || 'POS report'}</span>
              {extraction.posSystem && (
                <span style={{ fontSize: '0.7rem', background: '#5A4A42', color: '#fff', borderRadius: 8, padding: '1px 8px', fontWeight: 600 }}>
                  {extraction.posSystem}
                </span>
              )}
              {(extraction.periodStart || extraction.periodEnd) && (
                <span style={{ fontSize: '0.74rem', color: '#8B7B73', fontWeight: 500 }}>
                  {extraction.periodStart || '…'} → {extraction.periodEnd || '…'}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {cloverConnected && (
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ fontSize: '0.74rem', borderRadius: 8, background: '#00A551', color: '#fff', border: 'none', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,165,81,0.25)' }}
                  onClick={pullClover}
                  disabled={cloverBusy || busy}
                  title={cloverPeriodStart && cloverPeriodEnd ? `Re-pull ${cloverPeriodStart} → ${cloverPeriodEnd} from Clover` : 'Pull sales from Clover'}
                >
                  {cloverBusy ? 'Pulling…' : '🍀 Re-pull from Clover'}
                </button>
              )}
              {squareConnected && (
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ fontSize: '0.74rem', borderRadius: 8, background: '#005FA3', color: '#fff', border: 'none', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,95,163,0.25)' }}
                  onClick={pullSquare}
                  disabled={squareBusy || busy}
                  title={squarePeriodStart && squarePeriodEnd ? `Re-pull ${squarePeriodStart} → ${squarePeriodEnd} from Square` : 'Pull sales from Square'}
                >
                  {squareBusy ? 'Pulling…' : '🟦 Re-pull from Square'}
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: '0.74rem', borderRadius: 8 }}
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                {busy ? 'Reading…' : 'Upload a different file'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link"
                style={{ fontSize: '0.74rem', color: '#7A1F18', padding: '0 4px' }}
                onClick={onClear}
              >
                Remove
              </button>
            </div>
          </div>

          {cloverError && (
            <div style={{ fontSize: '0.78rem', color: '#7A1F18', background: '#FBEAEA', borderRadius: 7, padding: '0.4rem 0.75rem', marginBottom: '0.6rem' }}>
              {cloverError}
            </div>
          )}

          {totalsChips.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.6rem' }}>
              {totalsChips.map((c) => (
                <div key={c.label} style={{ background: '#fff', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.4rem 0.7rem', minWidth: 110 }}>
                  <div style={{ fontSize: '0.64rem', color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{c.label}</div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#3D2518' }}>{formatCurrency(c.value)}</div>
                </div>
              ))}
              {ignoredChips.map((c) => (
                <div key={c.label} title="Recognized in the report but never counted as sales" style={{ background: '#F7F3EF', border: '1px dashed #D4C4B0', borderRadius: 10, padding: '0.4rem 0.7rem', minWidth: 110, opacity: 0.8 }}>
                  <div style={{ fontSize: '0.64rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{c.label} — not imported</div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#8B7B73' }}>{formatCurrency(c.value)}</div>
                </div>
              ))}
            </div>
          )}

          {(extraction.warnings.length > 0 || extraction.categorySalesBasis === 'net') && (
            <div style={{ fontSize: '0.74rem', color: '#664D03', background: '#FFFBE6', border: '1px solid #FFE69C', borderRadius: 8, padding: '0.4rem 0.7rem', marginBottom: '0.6rem' }}>
              {extraction.categorySalesBasis === 'net' && (
                <div>The report's category amounts appear to be <strong>net of discounts</strong> — review the gross sales figures after applying.</div>
              )}
              {extraction.warnings.map((w) => <div key={w}>{w}</div>)}
            </div>
          )}

          {showMapper && (
            <PosReportCategoryMapper
              storeId={storeId}
              pnlCategories={pnlCategories || []}
              lines={mapperLines}
              onLink={(idx, v) => {
                onMappingChange({ ...(mapping || {}), [idx]: v });
                const cat = extraction.categories[idx];
                if (cat) persistMappingChoice(cat.name, v || null);
              }}
              onCategoryAdded={onCategoryAdded}
              onClose={() => setShowMapper(false)}
            />
          )}

          {extraction.categories.length > 0 && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '0.6rem' }}>
              <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.8rem', minWidth: 560, background: '#fff' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 140 }}>Category on Report</th>
                    <th className="text-end" style={{ minWidth: 100 }}>Sales</th>
                    <th style={{ minWidth: 150 }}>
                      Imports Into
                      {!showMapper && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link"
                          style={{ fontSize: '0.7rem', padding: '0 0 0 8px', color: '#1a4a7a', fontWeight: 600, textTransform: 'none' }}
                          onClick={() => setShowMapper(true)}
                        >
                          Edit Mapping
                        </button>
                      )}
                    </th>
                    <th style={{ minWidth: 90 }}>Match</th>
                  </tr>
                </thead>
                <tbody>
                  {extraction.categories.map((cat, idx) => {
                    const choice = choiceFor(idx, cat);
                    const explicitNone = Object.prototype.hasOwnProperty.call(mapping || {}, idx) && !mapping[idx];
                    // The effective choice came from (or was just written to)
                    // the store's permanent report-name mapping.
                    const savedKey = posCategoryKey(cat.name);
                    const fromSaved = Object.prototype.hasOwnProperty.call(savedMappings, savedKey)
                      && Object.prototype.hasOwnProperty.call(mapping || {}, idx)
                      && (savedMappings[savedKey] ?? '') === (mapping[idx] || '');
                    return (
                      <tr key={`${cat.name}-${idx}`}>
                        <td className="fw-semibold" style={{ color: '#3D2518' }}>
                          {cat.name}
                          {(cat.discounts != null || cat.comps != null || cat.refunds != null) && (
                            <div style={{ fontSize: '0.66rem', fontWeight: 400, color: '#8B7B73' }}>
                              {[
                                cat.discounts != null ? `discounts ${formatCurrency(cat.discounts)}` : null,
                                cat.comps != null ? `comps ${formatCurrency(cat.comps)}` : null,
                                cat.refunds != null ? `refunds ${formatCurrency(cat.refunds)}` : null,
                              ].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td className="text-end">{cat.grossSales != null ? formatCurrency(cat.grossSales) : '—'}</td>
                        <td>
                          {choice ? (
                            <span style={{ fontSize: '0.72rem', color: '#1a5c34', background: '#C8EED4', borderRadius: 10, padding: '2px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              → {catNameById[choice] || choice}
                            </span>
                          ) : explicitNone ? (
                            <span style={{ fontSize: '0.72rem', color: '#8B7B73', background: '#EDE6DE', borderRadius: 10, padding: '2px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              not imported
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.72rem', color: '#A0522D', background: '#FFF3E2', border: '1px solid #E0A878', borderRadius: 10, padding: '2px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              needs match
                            </span>
                          )}
                        </td>
                        <td>
                          {fromSaved
                            ? (
                              <span title="You set this match in a previous import — it's remembered and applied automatically. Use Edit Mapping to change it." style={{ fontSize: '0.66rem', padding: '1px 7px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap', background: '#E3EDF6', color: '#1a4a7a' }}>
                                saved mapping
                              </span>
                            )
                            : choice && choice !== cat.matchedCatId
                              ? (
                                <span style={{ fontSize: '0.66rem', padding: '1px 7px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap', background: '#E8DDD0', color: '#5A4A42' }}>
                                  your choice
                                </span>
                              )
                              : <ConfidenceBadge level={choice ? cat.confidence : 'none'} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {plan && (
            <div style={{ fontSize: '0.76rem', color: '#5A4A42', background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.7rem', lineHeight: 1.6 }}>
              <strong>Applying will fill:</strong>{' '}
              {plan.appliedCategoryCount > 0
                ? `gross sales for ${Object.entries(plan.byCat).filter(([, t]) => t.gross != null).map(([id]) => catNameById[id] || id).join(', ')}`
                : 'no category sales (match at least one report line above)'}
              {['discounts', 'comps', 'refunds'].some((f) => (plan.unallocated[f] ?? 0) > 0) && (
                <>
                  {'; '}
                  {['discounts', 'comps', 'refunds']
                    .filter((f) => (plan.unallocated[f] ?? 0) > 0)
                    .map((f) => `${f} ${formatCurrency(plan.unallocated[f])}`)
                    .join(', ')}{' '}
                  → <em>Unallocated Adjustments</em> (the report doesn't say which category they belong to)
                </>
              )}
              {plan.skipped.length > 0 && (
                <div style={{ color: '#856404' }}>
                  Not imported: {plan.skipped.map((s) => `${s.name} (${formatCurrency(s.amount)})`).join(', ')}
                </div>
              )}
            </div>
          )}

          {extraction.categories.length > 0 && !showMapper && (
            <button
              type="button"
              className="btn"
              style={{
                background: '#fff', color: BROWN, borderRadius: 10, fontWeight: 700,
                padding: '0.45rem 1.25rem', fontSize: '0.85rem', border: `1.5px solid ${BROWN}`,
                marginRight: 10,
              }}
              onClick={() => setShowMapper(true)}
            >
              Edit Mapping
            </button>
          )}
          <button
            type="button"
            className="btn"
            style={{
              background: canApply ? '#2c7a4b' : '#c9bdb6', color: '#fff', borderRadius: 10,
              fontWeight: 700, padding: '0.45rem 1.25rem', fontSize: '0.85rem', border: 'none',
              cursor: canApply ? 'pointer' : 'not-allowed',
            }}
            disabled={!canApply}
            onClick={onApply}
          >
            {applied ? 'Re-apply to Report' : 'Apply to Report'}
          </button>
          {applied && (
            <span style={{ fontSize: '0.74rem', color: '#1a5c34', marginLeft: 10 }}>
              ✓ Applied {new Date(applied.appliedAt).toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EOPPosReportImport;
